/**
 * Was das Büro an einem Einsatz und seinem Bericht entscheidet — `PORTAL-002`.
 *
 * **Hier schließt sich der Kreis.** Der Monteur übermittelt seinen Bericht,
 * das Büro prüft ihn und gibt ihn frei oder zurück. Ohne diese Ansicht endet
 * der Ablauf aus `07` §7.8 in einer Sackgasse: `uebermittelt → geprueft` und
 * `uebermittelt → abschluss` gehören nach ADR-045 ausschließlich dem Büro, und
 * es hatte keinen Ort, an dem es sie auslösen konnte.
 *
 * **Zwei Prüfungen, immer beide.** `uebergangErlaubt` sagt, ob der Weg
 * existiert; `darfSchalten` sagt, ob diese Rolle ihn gehen darf. Angeboten
 * wird nur, was beide erlauben — und was das Büro **stellvertretend** setzt,
 * sagt das im Knopf, nicht erst im Protokoll.
 */

import { useState } from "react";

import { darfBerichtFreigeben, darfSchalten } from "@hwc/auth";
import { findePerson } from "@hwc/demo-data";
import type { Einsatz, Servicebericht } from "@hwc/demo-data";
import { Dialog, Knopf, Zustand } from "@hwc/ui";
import type { EinsatzZustand } from "@hwc/sync";
import { UEBERGAENGE, uebergangErlaubt } from "@hwc/sync";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { entscheideBericht, schalteEinsatz } from "../../src/daten/vorgaenge.js";

/**
 * Wie ein Übergang aus Bürosicht heißt.
 *
 * Nicht dieselben Wörter wie in der Monteur-App: Dort steht „Losfahren", weil
 * der Monteur losfährt. Hier steht „Anfahrt eintragen", weil das Büro sie
 * einträgt — es fährt nicht. Ein gemeinsamer Wortschatz für beide Seiten
 * klänge in einer der beiden Oberflächen immer falsch.
 */
const BUEROWORT: Record<EinsatzZustand, string> = {
  geplant: "Zurück auf geplant",
  anfahrt: "Anfahrt eintragen",
  "vor-ort": "Ankunft eintragen",
  wartet: "Als wartend eintragen",
  abschluss: "Arbeit als fertig eintragen",
  uebermittelt: "Als übermittelt eintragen",
  geprueft: "Prüfen und abschließen",
  storniert: "Einsatz stornieren",
};

export function Einsatzsteuerung({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, person, rollen, fuehreAus } = useSitzung();
  const [absage, setzeAbsage] = useState<string | null>(null);
  const [stornoOffen, setzeStorno] = useState(false);

  const monteur = findePerson(bestand, einsatz.monteur);

  /**
   * Die Übergänge, die sowohl fachlich möglich als auch erlaubt sind.
   *
   * Ein Knopf, der beim Drücken eine Absage bringt, ist schlechter als keiner:
   * Er sieht aus wie ein Angebot. Ausgegraute Knöpfe wären dasselbe in
   * langsam — deshalb erscheint hier nur, was tatsächlich geht.
   */
  const moeglich = UEBERGAENGE[einsatz.zustand]
    .filter((nach) => uebergangErlaubt(einsatz.zustand, nach))
    .map((nach) => ({
      nach,
      recht: darfSchalten({
        von: einsatz.zustand,
        nach,
        rollen,
        istZugewiesen:
          einsatz.monteur !== undefined
          && einsatz.monteur.opaqueId === person.ref.opaqueId,
      }),
    }))
    .filter((e) => e.recht !== "nein");

  const schalten = (nach: EinsatzZustand) => {
    const grund = fuehreAus((v) =>
      schalteEinsatz({
        vorgaenge: v,
        einsatzRef: einsatz.ref,
        von: einsatz.zustand,
        nach,
        akteur: person.ref,
        rollen,
        zugewiesen: einsatz.monteur,
      }),
    );
    setzeAbsage(grund ?? null);
  };

  if (moeglich.length === 0) {
    return (
      <p className="notiz">
        {einsatz.zustand === "geprueft"
          ? "Geprüft und abgeschlossen. Von hier führt kein Weg mehr weg."
          : einsatz.zustand === "storniert"
            ? "Storniert. Ein stornierter Einsatz wird nicht wiederbelebt — ein "
              + "neuer Anlauf ist ein neuer Einsatz."
            : "Ihre Rolle kann an diesem Einsatz nichts schalten."}
      </p>
    );
  }

  const normal = moeglich.filter((e) => e.nach !== "storniert");
  const storno = moeglich.find((e) => e.nach === "storniert");

  return (
    <>
      {absage && <Zustand art="warnung" text={absage} />}

      <div className="filter">
        {normal.map(({ nach, recht }) => (
          <Knopf
            key={nach}
            type="button"
            art={nach === "geprueft" ? "haupt" : "neben"}
            onClick={() => schalten(nach)}
          >
            {BUEROWORT[nach]}
            {recht === "stellvertretend" && " (für den Monteur)"}
          </Knopf>
        ))}
        {storno && (
          <Knopf type="button" art="still" onClick={() => setzeStorno(true)}>
            Stornieren
          </Knopf>
        )}
      </div>

      {normal.some((e) => e.recht === "stellvertretend") && (
        <p className="notiz">
          {/*
            ADR-045 nennt genau diesen Fall: „Er ruft an, statt zu tippen, und
            bei schlechtem Empfang ist das der Normalfall. Der Preis ist die
            Urheberschaft."
          */}
          <strong>Stellvertretend.</strong> Diesen Zustand setzt sonst der Monteur.
          Das Protokoll führt beide — Sie als Schaltenden und{" "}
          {monteur?.name ?? "den Zugewiesenen"} als den, der vor Ort ist.
        </p>
      )}

      <Dialog
        offen={stornoOffen}
        beiSchliessen={() => setzeStorno(false)}
        titel="Einsatz stornieren?"
      >
        <p>
          Der Einsatz wird storniert und ist danach endgültig — es gibt keinen Weg
          zurück. Ein neuer Anlauf wäre ein neuer Einsatz.
        </p>
        {/*
          Der Fall, den ADR-045 in Kauf nimmt und `09` §9.3 an `EXP-001`
          weitergibt. Er gehört **vor** die Entscheidung, nicht in eine
          Fußnote danach.
        */}
        <p>
          <strong>Bereits erfasste Zeiten und Materialposten bleiben stehen.</strong> Was
          bei der Übergabe an die Branchensoftware mit ihnen geschieht, ist nach{" "}
          <code>09</code> §9.3 nicht entschieden.
        </p>
        <div className="filter">
          <Knopf
            type="button"
            art="haupt"
            onClick={() => {
              schalten("storniert");
              setzeStorno(false);
            }}
          >
            Ja, stornieren
          </Knopf>
          <Knopf type="button" art="neben" onClick={() => setzeStorno(false)}>
            Abbrechen
          </Knopf>
        </div>
      </Dialog>
    </>
  );
}

/**
 * Die Entscheidung des Büros über einen Servicebericht — `07` §7.16.
 *
 * **Freigabe und Rückgabe hängen am Einsatz.** Gibt das Büro zurück, geht der
 * Bericht auf `entwurf` **und** der Einsatz von `uebermittelt` auf
 * `abschluss` — das ist der einzige Rückweg im Automaten, und er existiert
 * genau für diesen Fall. Beides getrennt zu schalten hieße, einen Zustand
 * herstellen zu können, in dem der Bericht zurück ist und der Einsatz nicht.
 *
 * **Die interne Notiz steht sichtbar dabei, und der Satz dazu auch.**
 * `07` §7.16 trennt Kundensicht und interne Notizen, „damit interne Chats oder
 * nicht freigegebene Medien nicht versehentlich im PDF erscheinen". Wer
 * freigibt, muss sehen, was er nicht freigibt.
 */
export function Berichtsentscheidung({
  bericht,
  einsatz,
}: {
  bericht: Servicebericht;
  einsatz: Einsatz | undefined;
}) {
  const { person, rollen, fuehreAus } = useSitzung();
  const [absage, setzeAbsage] = useState<string | null>(null);

  if (!darfBerichtFreigeben(rollen)) {
    return (
      <p className="notiz">
        Freigabe und Rückgabe entscheidet das Büro (<code>07</code> §7.16). Ihre Rolle
        kann den Bericht lesen.
      </p>
    );
  }

  const entscheiden = (
    entscheidung: "freigeben" | "veroeffentlichen" | "zurueckgeben",
  ) => {
    const grund = fuehreAus((v) => {
      const ergebnis = entscheideBericht({
        vorgaenge: v,
        bericht,
        entscheidung,
        akteur: person.ref,
        rollen,
      });
      if (!ergebnis.ok) return ergebnis;

      // Rückgabe zieht den Einsatz mit. Ohne das entstünde ein Bericht im
      // Entwurf an einem Einsatz, der weiter als übermittelt gilt — und der
      // Monteur sähe keinen Weg, ihn erneut zu senden.
      if (entscheidung === "zurueckgeben" && einsatz?.zustand === "uebermittelt") {
        return schalteEinsatz({
          vorgaenge: ergebnis.vorgaenge,
          einsatzRef: einsatz.ref,
          von: "uebermittelt",
          nach: "abschluss",
          akteur: person.ref,
          rollen,
          zugewiesen: einsatz.monteur,
        });
      }
      return ergebnis;
    });
    setzeAbsage(grund ?? null);
  };

  return (
    <>
      {absage && <Zustand art="warnung" text={absage} />}

      {/*
        **Was freigegeben wird, steht zuerst.**

        Bis zum 05.09.2026 zeigte diese Ansicht nur die interne Notiz. Das Büro
        sollte „Version 2 freigeben" drücken, ohne den Text gesehen zu haben,
        den der Kunde danach liest — `05` §5.12 verlangt, dass eine auslösende
        Aktion vorher sagt, was sie tut, und „Freigabe" ohne den freizugebenden
        Text sagt es nicht.

        Die Reihenfolge ist Absicht: erst die Kundensicht, dann das Interne.
        Wer von oben liest, sieht zuerst, worüber er entscheidet.
      */}
      <h3>Was der Kunde später liest</h3>
      <pre className="wissenstext">{bericht.taetigkeit}</pre>

      {bericht.interneNotiz && (
        <p className="notiz notiz--warnung">
          <strong>Interne Notiz — nicht im Kunden-PDF:</strong> {bericht.interneNotiz}
        </p>
      )}

      <div className="filter">
        {bericht.zustand === "zur_pruefung" && (
          <>
            <Knopf type="button" art="haupt" onClick={() => entscheiden("freigeben")}>
              Version {bericht.version} freigeben
            </Knopf>
            <Knopf type="button" art="neben" onClick={() => entscheiden("zurueckgeben")}>
              Zurückgeben
            </Knopf>
          </>
        )}
        {bericht.zustand === "freigegeben" && (
          <Knopf type="button" art="neben" onClick={() => entscheiden("veroeffentlichen")}>
            Für den Kunden veröffentlichen
          </Knopf>
        )}
      </div>

      {bericht.zustand === "zur_pruefung" && (
        <p className="notiz">
          {/*
            Der Satz, der im Dokument steht und den eine Oberfläche leicht
            unterschlägt.
          */}
          Die Freigabe gilt für <strong>Version {bericht.version}</strong>, nicht für
          den Bericht im Allgemeinen. Eine Rückgabe setzt den Einsatz zurück auf
          Abschluss — der Monteur kann dann nacharbeiten und erneut senden.
        </p>
      )}
    </>
  );
}
